import React, { useState, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUploadAlt,
  faTimes,
  faSpinner,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import useToastError from "../../../../hooks/useErrorToast";
import { FileApiUrls } from "../../../../Api/Auth/ApiUrls";
import { sendPostRequestAuth } from "../../../../Api/swrConfig";

const PasteImageUploader = ({
  label,
  name,
  onChange,
  value,
  placeholder = "Click here and paste your image (Ctrl+V) or (Cmd+V)",
  error,
  folder = "Screenshots",
  multiple = false,
  upload = false, // New prop
  uploadFolder = "uploads",
}) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const pasteAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const { showErrorToast } = useToastError();

  // Upload mutation
  const { trigger: uploadTrigger, isMutating: isUploading } = useSWRMutation(
    FileApiUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        if (onChange && data?.files?.[0]?.fileUrl) {
          if (multiple) {
            // For multiple files, add to array
            const currentImages = Array.isArray(value) ? value : [];
            const newImages = [...currentImages, data?.files?.[0]?.fileUrl];
            onChange(name, newImages);
          } else {
            // For single file, replace
            onChange(name, data?.files?.[0]?.fileUrl);
          }
        }
        setIsUploaded(true);
      },
      onError: (error) => {
        showErrorToast(error);
        handleClear();
      },
    }
  );

  // Handle file upload from PC
  const handleFileUpload = useCallback(
    async (file) => {
      if (!file || !file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Create preview URL (only for single mode)
      if (!multiple) {
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
      }
      setIsUploaded(false);

      // Upload to "uploads" folder
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", uploadFolder);

      await uploadTrigger(formData);
    },
    [uploadTrigger, multiple]
  );

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input value to allow re-uploading the same file
    e.target.value = "";
  };

  // Handle paste event - auto upload
  const handlePaste = useCallback(
    async (e) => {
      const items = e.clipboardData?.items;

      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();

          if (file) {
            // Generate a filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
            const generatedFileName = `screenshot-${timestamp}.png`;

            // Create a new File object with the generated name
            const renamedFile = new File([file], generatedFileName, {
              type: file.type,
              lastModified: Date.now(),
            });

            // Create preview URL (only for single mode)
            if (!multiple) {
              const previewUrl = URL.createObjectURL(file);
              setImagePreview(previewUrl);
            }
            setIsUploaded(false);

            // Auto upload to folder prop
            const formData = new FormData();
            formData.append("file", renamedFile);
            formData.append("folder", folder);

            await uploadTrigger(formData);
          }
          break;
        }
      }
    },
    [uploadTrigger, folder, multiple]
  );

  // Clear pasted image
  const handleClear = () => {
    setImagePreview(null);
    setIsUploaded(false);
    if (onChange) {
      if (multiple) {
        onChange(name, []);
      } else {
        onChange(name, "");
      }
    }
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
  };

  // Remove individual image (for multiple mode)
  const handleRemoveImage = (imageUrl) => {
    if (multiple && Array.isArray(value)) {
      const newImages = value.filter((img) => img !== imageUrl);
      onChange(name, newImages);
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    if (upload && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Show uploaded image from value prop
  const displayImage = multiple ? null : value || imagePreview;
  const displayImages = multiple ? (Array.isArray(value) ? value : []) : [];

  return (
    <div className="mb-4">
      {label && (
        <label className="text-gray-700 mb-2 font-regular flex items-center text-sm">
          {label}
        </label>
      )}

      {/* Hidden file input */}
      {upload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
        />
      )}

      {/* Paste Area / Image Display */}
      <div className="relative">
        {/* Multiple Images Display */}
        {multiple && displayImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            {displayImages.map((imageUrl, index) => (
              <div key={index} className="relative inline-block">
                <img
                  src={imageUrl}
                  alt={`Uploaded ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg shadow-md border"
                />
                <button
                  onClick={() => handleRemoveImage(imageUrl)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200 flex items-center justify-center"
                  disabled={isUploading}
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Single Image Display */}
        {!multiple && displayImage ? (
          // Image Display with Cross Button
          <div className="relative inline-block">
            <img
              src={displayImage}
              alt="Uploaded preview"
              className="max-w-full max-h-64 rounded-lg shadow-md border"
            />

            {/* Cross button */}
            <button
              onClick={handleClear}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200 flex items-center justify-center"
              disabled={isUploading}
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>

            {/* Upload Status Overlay */}
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                <div className="text-white text-center">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin text-2xl mb-2"
                  />
                  <p className="text-sm">Uploading...</p>
                </div>
              </div>
            )}

            {/* Success Indicator */}
            {isUploaded && !isUploading && (
              <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-md text-xs flex items-center gap-1">
                <FontAwesomeIcon icon={faCheckCircle} />
                Uploaded
              </div>
            )}
          </div>
        ) : null}

        {/* Paste Area - Show if single mode with no image, or multiple mode */}
        {(multiple || !displayImage) && (
          // Paste Area
          <div
            ref={pasteAreaRef}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 outline-none hover:border-primary hover:bg-blue-50 focus:border-primary focus:bg-blue-50"
            tabIndex={0}
            onPaste={handlePaste}
            onClick={() => pasteAreaRef.current?.focus()}
          >
            <FontAwesomeIcon
              icon={faCloudUploadAlt}
              className="text-4xl text-gray-400 mb-3"
            />
            <p className="text-gray-700 text-sm font-medium mb-1">
              {multiple
                ? `${placeholder} ${
                    displayImages.length > 0 ? "(Add more images)" : ""
                  }`
                : placeholder}
            </p>
            {upload && (
              <p className="text-gray-500 text-xs mt-1">
                or{" "}
                <button
                  type="button"
                  onClick={handleUploadClick}
                  className="text-primary hover:underline font-medium"
                  disabled={isUploading}
                >
                  browse files
                </button>
              </p>
            )}

            {/* Upload Status for Multiple */}
            {multiple && isUploading && (
              <div className="mt-4 text-center">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin text-xl text-primary"
                />
                <p className="text-sm text-gray-600 mt-2">Uploading...</p>
              </div>
            )}
          </div>
        )}

        {/* Clear All Button for Multiple */}
        {multiple && displayImages.length > 0 && (
          <div className="mt-4 text-center">
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-200 text-sm"
              disabled={isUploading}
            >
              Clear All Images
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default PasteImageUploader;
